'use client';

/**
 * ContactSupportDialog Component
 *
 * A responsive dialog that wraps the AskQueryForm for contacting support.
 * Shows as a dialog on desktop and a drawer/sheet on mobile.
 * Can optionally include booking context for support requests related to bookings.
 */
import { useState } from 'react';
import { toast } from 'sonner';

import { ResponsiveDialog } from '../../../common/dialog/responsiveDialog';
import { AskQueryForm, AskQueryFormValues } from '../../landing/forms/AskQueryForm';

export interface ContactSupportDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Callback when the dialog should be closed
   */
  onClose: () => void;
  /**
   * Optional booking ID for context (e.g., when contacting support about a specific booking)
   */
  bookingId?: string;
  /**
   * Default email to pre-fill the form (e.g., user's email)
   */
  defaultEmail?: string;
  /**
   * Callback to handle form submission
   * Should handle file uploads and API calls
   */
  onSubmit: (data: AskQueryFormValues, attachments?: File[], bookingId?: string) => Promise<void>;
  /**
   * Translations for the dialog and form
   */
  translations?: {
    // Form translations
    title?: string;
    email?: string;
    emailPlaceholder?: string;
    question?: string;
    questionPlaceholder?: string;
    attachment?: string;
    attachmentPrompt?: string;
    attachmentInfoPrefix?: string;
    attachmentInfoSuffix?: string;
    attachmentInfoSuffix2?: string;
    maxImagesWarningPrefix?: string;
    maxImagesWarningSuffix?: string;
    submitButton?: string;
    submittingButton?: string;
    responseTime?: string;
    emailError?: string;
    questionError?: string;
    // Success/error messages
    successMessage?: string;
    successDescription?: string;
    errorMessage?: string;
  };
}

/**
 * ContactSupportDialog - A dialog for contacting support
 *
 * @example
 * ```tsx
 * <ContactSupportDialog
 *   isOpen={showSupportDialog}
 *   onClose={() => setShowSupportDialog(false)}
 *   bookingId="booking_123"
 *   defaultEmail="user@example.com"
 *   onSubmit={async (data, attachments, bookingId) => {
 *     // Upload attachments and call API
 *   }}
 * />
 * ```
 */
export const ContactSupportDialog = ({
  isOpen,
  onClose,
  bookingId,
  defaultEmail,
  onSubmit,
  translations
}: ContactSupportDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: AskQueryFormValues, attachments?: File[]) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data, attachments, bookingId);
      toast.success(translations?.successMessage || 'Message sent!', {
        description: translations?.successDescription || 'We will reply to you via email shortly.'
      });
      onClose();
    } catch (error) {
      console.error('Failed to send support request:', error);
      toast.error(translations?.errorMessage || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      size="default"
      mobileSide="bottom"
      className="p-0"
      showCloseButton={true}
    >
      <div className="p-5">
        <AskQueryForm
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          onCancel={onClose}
          userEmail={defaultEmail}
          translations={{
            title: translations?.title || 'Contact Support',
            email: translations?.email,
            emailPlaceholder: translations?.emailPlaceholder,
            question: translations?.question,
            questionPlaceholder: translations?.questionPlaceholder,
            attachment: translations?.attachment,
            attachmentPrompt: translations?.attachmentPrompt,
            attachmentInfoPrefix: translations?.attachmentInfoPrefix,
            attachmentInfoSuffix: translations?.attachmentInfoSuffix,
            attachmentInfoSuffix2: translations?.attachmentInfoSuffix2,
            maxImagesWarningPrefix: translations?.maxImagesWarningPrefix,
            maxImagesWarningSuffix: translations?.maxImagesWarningSuffix,
            submitButton: translations?.submitButton || 'Send Message',
            submittingButton: translations?.submittingButton || 'Sending...',
            responseTime: translations?.responseTime,
            emailError: translations?.emailError,
            questionError: translations?.questionError
          }}
        />
      </div>
    </ResponsiveDialog>
  );
};

export default ContactSupportDialog;
